/** Max length of a full DNS hostname. */
export const DOMAIN_MAX_LENGTH = 253;

/** A single DNS label: alphanumeric, hyphens allowed inside, 1-63 chars. */
export const DNS_LABEL_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

export const isDnsLabel = (label: string) => DNS_LABEL_PATTERN.test(label);

export type DomainParts = {
  fullDomain: string;
  subdomainLabel: string;
  rootDomain: string;
};

export const parseDomainParts = (input: string): DomainParts | null => {
  const normalized = input.trim().toLowerCase().replace(/\.$/, '');

  if (normalized.length === 0 || normalized.length > DOMAIN_MAX_LENGTH) {
    return null;
  }

  const labels = normalized.split('.');
  if (labels.length < 2 || labels.some((label) => !isDnsLabel(label))) {
    return null;
  }

  // A top-level corporate domain
  if (labels.length === 2) {
    return {
      fullDomain: normalized,
      subdomainLabel: '',
      rootDomain: normalized,
    };
  }

  const firstDot = normalized.indexOf('.');
  return {
    fullDomain: normalized,
    subdomainLabel: normalized.slice(0, firstDot),
    rootDomain: normalized.slice(firstDot + 1),
  };
};

const RESERVED_TOP_LEVEL_LABELS = ['localhost', 'local', 'internal'];

export function isPublicHostname(hostname: string) {
  const labels = hostname.replace(/\.$/, '').split('.');
  // URL parsing normalizes every IPv4 notation to dotted decimal and brackets IPv6.
  const isIpAddress = hostname.startsWith('[') || /^\d+\.\d+\.\d+\.\d+$/.test(hostname);
  const topLevelLabel = labels.at(-1) ?? '';
  return !isIpAddress && labels.length > 1 && !RESERVED_TOP_LEVEL_LABELS.includes(topLevelLabel);
}
